import React, { useEffect, useState } from "react";
import { HeaderComponent } from "../../components/header/header.component";
import { preLoadImagePath } from '../../helpers/images';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { headerConstants } from '../../redux/header/header.constants';
import { setHeader } from '../../redux/header/header.actions';
import { vippsService } from '../../services/api/vipps.service';
import { toastr } from 'react-redux-toastr';
import { deviceConstants } from '../../redux/device/device.constants';
import { setDeviceData } from '../../redux/device/device.actions';
import { deviceService } from '../../services/api/device.service'
import { queryParamValue, isObjectEmpty } from '../../helpers/helpers';
import { AppConstant, DeviceClassEnum } from '../../helpers/constants';
import { localStorageService } from '../../services/utilities/localStorageService';
import { sessionStorageService } from '../../services/utilities/sessionStorageService';
import { vippsStateManager } from '../../services/utilities/vippsStateManager';
import { getVippsErrorMessage, isErrorRetryable } from '../../helpers/vippsErrorCodes';
import { retryWithBackoff, isRetryableError } from '../../services/utilities/retryHelper';
import { logger } from '../../services/utilities/logger';
import { ErrorsComponent } from '../../components/error/errors.component';
import { errorConstants } from '../../redux/error/error.constants';
import { setAPIError, setError } from '../../redux/error/error.actions';
import {sessionManager} from "../../services/utilities/sessionManager";
import { offlineDetector } from '../../services/utilities/offlineDetector';
import { FooterComponent } from '../../components/footer/footer.component';

// Module-level variable to track processing state
let isProcessingVippsCallback = false;

function HomePage() {
    const dispatch = useDispatch();
    const history = useHistory();
    const [headerReducer, errorReducer] = useSelector(state => [state.headerReducer, state.errorReducer]);
    const [infoMessage, setInfoMessage] = useState();
    const [isLoader, setIsLoader] = useState(true);
    const [userInfo, setUserInfo] = useState();
    const [timeoutWarning, setTimeoutWarning] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Verifiserer innlogging...');
    const [isOffline, setIsOffline] = useState(false);
    const vippsDefaultErrorMessage = ['wrong_challenge', 'access_denied'];

    const clearUrlParameters = () => {
        try {
            var url = new URL(window.location.href);
            url.searchParams.delete('code');
            url.searchParams.delete('state');
            window.history.replaceState({}, '', url.pathname);
        } catch (error) {
            console.warn('Error clearing URL parameters:', error);
        }
    }

    const redirectToStartAuthentication = () => {
        try {
            history.push("/scan");
        } catch (error) {
            console.warn('Error redirecting to start authentication:', error);
            window.location.href = "/scan";
        }
    }

    useEffect(() => {
        // Clean up stale sessionStorage on component mount
        vippsStateManager.cleanupStaleSessionStorage();
        
        // Set initial offline state
        setIsOffline(offlineDetector.isOffline());
        
        // Subscribe to offline/online events
        const unsubscribeOffline = offlineDetector.onOffline(() => {
            setIsOffline(true);
            if (isProcessingVippsCallback) {
                toastr.warning('Ingen internettforbindelse', 'Sjekk internettforbindelsen din og prøv igjen.');
            }
        });
        
        const unsubscribeOnline = offlineDetector.onOnline(() => {
            setIsOffline(false);
            if (isProcessingVippsCallback) {
                toastr.success('Internettforbindelse gjenopprettet', 'Du kan nå fortsette.');
            }
        });
        
        // Check for redirect timeout
        var redirectTime = sessionStorageService.getRedirectTime();
        if (redirectTime) {
            var elapsed = (Date.now() - parseInt(redirectTime)) / 1000 / 60; // minutes
            if (elapsed > 2) {
                // More than 2 minutes since redirect - likely timeout
                console.warn('Possible redirect timeout detected. Elapsed:', elapsed.toFixed(2), 'minutes');
                toastr.error('Redirect timeout', 'Det ser ut til at redirect til Vipps tok for lang tid. Vennligst skann QR-koden på nytt.');
                sessionStorageService.removeRedirectTracking();
                dispatch(setAPIError(errorConstants.SET_ERROR_API, true, true, 400));
                redirectToStartAuthentication();
            }
        }
        
        // Page Visibility API listener for tab reuse detection
        var handleVisibilityChange = function() {
            if (document.visibilityState === 'visible') {
                try {
                    // Don't interfere if callback is currently being processed
                    if (isProcessingVippsCallback) {
                        logger.debug('Visibility change detected but callback is being processed, ignoring');
                        return;
                    }
                    
                    var urlParams = new URLSearchParams(window.location.search);
                    var code = urlParams.get('code');
                    var state = urlParams.get('state');
                    
                    if (code && state) {
                        // Check if code has already been processed
                        if (vippsStateManager.isCodeProcessed(code)) {
                            logger.warn('Tab reactivated with already-processed code');
                            clearUrlParameters();
                            redirectToStartAuthentication();
                        }
                    }
                } catch (error) {
                    logger.warn('Error in visibility change handler', { error: error });
                }
            }
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        // Main callback handling
        try {
            var urlParams = new URLSearchParams(window.location.search);
            var code = urlParams.get('code');
            var state = urlParams.get('state');
            
            if (code || state) {
                // Clear redirect timestamp on successful callback
                sessionStorageService.removeRedirectTracking();
                
                // Handle Vipps callback
                dispatch(setHeader(headerConstants.SET_HEADER, true, null));
                vippsCodeValidate(code, state);
            } else {
                // No callback parameters, check for existing user info
                // CRITICAL: Only accept userInfo from actual Vipps authentication, not from Device endpoint
                // Device endpoint should never set pageType = success (that's fixed in device.jsx)
                // But we also validate here as defense-in-depth
                setIsLoader(false);
                var userDetail = headerReducer?.userInfo;
                if (userDetail !== null && !isObjectEmpty(userDetail)) {
                    // Only accept userInfo from Vipps authentication (loginType === 'Vipps')
                    // Reject userInfo from Device endpoint (loginType === 'DeviceClass')
                    // This prevents showing "Døren er låst opp" without actual authentication
                    if (userDetail.loginType === AppConstant.vippsAppName) {
                        setUserInfo(userDetail);
                        if (userDetail.message !== "" && userDetail.pageType === 'Error') {
                            setInfoMessage(userDetail.message)
                        }
                    } else {
                        // userInfo is from Device endpoint or other non-Vipps source - clear it
                        console.warn('Rejecting userInfo from non-Vipps source:', userDetail.loginType);
                        dispatch(setError(errorConstants.SET_ERROR, true));
                    }
                } else {
                    dispatch(setError(errorConstants.SET_ERROR, true));
                }
            }
        } catch (exception) {
            console.error('Error in useEffect:', exception);
            setIsLoader(false);
        }
        
        // Cleanup function
        return function() {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            unsubscribeOffline();
            unsubscribeOnline();
        };
    }, []);


    const errorVipps = (errorMessage) => {
        return vippsDefaultErrorMessage.includes(errorMessage);
    }

    const vippsCodeValidate = async (code, state) => {
        // Guard: prevent multiple simultaneous processing
        if (isProcessingVippsCallback) {
            logger.warn('Already processing Vipps callback, ignoring duplicate');
            return;
        }
        
        isProcessingVippsCallback = true;
        setUserInfo();
        logger.logVippsEvent('callback_received', { hasCode: !!code, hasState: !!state });
        
        // Declare timeout timer at function scope for cleanup in catch
        var timeoutWarningTimer = null;
        
        try {
            // Handle Vipps error responses with improved error mapping
            var errorModel = {};
            if (code && errorVipps(code)) {
                setIsLoader(false);
                // Use improved error code mapping
                var errorInfo = getVippsErrorMessage(code);
                errorModel = {
                    error: code,
                    errorDescription: errorInfo.message,
                    userAction: errorInfo.userAction,
                    retryable: errorInfo.retryable,
                    severity: errorInfo.severity
                };
                clearUrlParameters();
                isProcessingVippsCallback = false;
                return;
            }
            
            // HENT DeviceUuid - prøv flere kilder for cross-browser support
            var deviceUUID = null;
            var stateData = null;
            var trackingInfo = {
                hasState: !!state,
                hasCode: !!code,
                sources: []
            };
            
            // 1. Prøv å hente fra state parameter (fra callback URL)
            if (state) {
                try {
                    stateData = vippsStateManager.parseState(state);
                    if (stateData && stateData.deviceUuid) {
                        deviceUUID = stateData.deviceUuid;
                        trackingInfo.sources.push('state_parameter');
                        logger.debug('DeviceUuid found in state parameter', { deviceUUID });
                        
                        // Tracking: Valider state hvis tilgjengelig (ikke blokkerende)
                        var fullStateData = {
                            state: state,
                            timestamp: stateData.timestamp,
                            nonce: stateData.nonce
                        };
                        
                        if (vippsStateManager.isStateValid(fullStateData, deviceUUID, 10)) {
                            trackingInfo.stateValid = true;
                        } else {
                            trackingInfo.stateValid = false;
                            logger.warn('State validation failed (non-blocking)', { deviceUUID: deviceUUID });
                        }
                        
                        // Tracking: Sjekk timestamp (ikke blokkerende)
                        if (vippsStateManager.isCallbackStale(stateData.timestamp, 5)) {
                            trackingInfo.stateStale = true;
                            logger.warn('State is stale (non-blocking)', { timestamp: stateData.timestamp });
                        } else {
                            trackingInfo.stateStale = false;
                        }
                    }
                } catch (e) {
                    logger.warn('Failed to parse state parameter (non-blocking)', { state: state, error: e });
                }
            }
            
            // 2. Fallback: Hent fra sessionStorage
            if (!deviceUUID) {
                try {
                    var storedDeviceUuid = sessionStorageService.getDeviceUuid();
                    if (storedDeviceUuid && storedDeviceUuid.trim() !== '') {
                        deviceUUID = storedDeviceUuid.trim();
                        trackingInfo.sources.push('sessionStorage');
                        logger.debug('DeviceUuid found in sessionStorage', { deviceUUID });
                    }
                } catch (e) {
                    logger.warn('Failed to get DeviceUuid from sessionStorage', { error: e });
                }
            }
            
            // Valider DeviceUuid format (basic UUID validation)
            var isValidUUID = deviceUUID && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(deviceUUID);
            
            // Hvis vi fortsatt ikke har DeviceUuid eller det er ugyldig, kan vi ikke fortsette
            if (!deviceUUID || !isValidUUID) {
                logger.error('DeviceUuid not found or invalid. Cannot proceed.', { 
                    deviceUUID: deviceUUID,
                    isValidUUID: isValidUUID,
                    trackingInfo: trackingInfo,
                    url: window.location.href 
                });
                toastr.error('Noe er ikke satt opp riktig på din mobiltelefon, og den vil ikke fortelle oss hvilken dør du ønsker å låse opp.');
                // Rydd opp DeviceUuid og session data hvis vi ikke kan fortsette (IKKE telefonnummer)
                try {
                    sessionStorageService.removeDeviceUuid();
                    sessionManager.clearSession(); // Rydd opp session data
                } catch (e) {
                    logger.warn('Failed to clean up storage (non-blocking)', { error: e });
                }
                clearUrlParameters();
                redirectToStartAuthentication();
                isProcessingVippsCallback = false;
                return;
            }
            
            // Log tracking info for debugging
            logger.info('DeviceUuid resolved for callback', {
                deviceUUID: deviceUUID,
                sources: trackingInfo.sources,
                hasState: trackingInfo.hasState,
                stateValid: trackingInfo.stateValid,
                stateStale: trackingInfo.stateStale
            });
            
            // Tracking: Sjekk code replay på frontend (ikke blokkerende - backend håndterer dette)
            if (code && vippsStateManager.isCodeProcessed(code)) {
                trackingInfo.codeAlreadyProcessed = true;
                logger.warn('Code already processed in frontend (backend will handle)', { code: code.substring(0, 10) + '...' });
            } else {
                trackingInfo.codeAlreadyProcessed = false;
            }
            
            logger.logStateTransition('callback_received', 'authenticating', { deviceUUID: deviceUUID, sources: trackingInfo.sources });
            
            // Get device info
            setLoadingMessage('Henter enhetsinformasjon...');
            var deviceData = await deviceInfo(deviceUUID);
            if (deviceData === null || deviceData === "") {
                toastr.warning(AppConstant.invalidDeviceId);
                // Check if it's a timeout error (400)
                var isTimeout = deviceData && deviceData.error === 'timeout';
                dispatch(setAPIError(errorConstants.SET_ERROR_API, true, true, isTimeout ? 400 : null));
                vippsStateManager.clearVippsState(deviceUUID, code);
                clearUrlParameters();
                isProcessingVippsCallback = false;
                return;
            }

            var deviceTypeClass = deviceData.device?.deviceType?.deviceClass.toLowerCase();
            if (deviceTypeClass !== DeviceClassEnum.none.toLowerCase()) {
                document.getElementById("root").classList.add(deviceTypeClass);
            }

            // Call AfterAuthentication API
            setLoadingMessage('Verifiserer med Vipps...');
            
            // Hent telefonnummer (kan være null i cross-browser scenarios)
            var phoneNumber = localStorageService.getVippsMobileNo();
            if (!phoneNumber) {
                logger.warn('PhoneNumber not found in localStorage (cross-browser scenario)', { deviceUUID: deviceUUID });
            }
            
            var model = {
                code: isObjectEmpty(errorModel) ? code : "",
                deviceUuid: deviceUUID,
                state: state || "",  // Optional - send empty string if not available
                ...errorModel,
                phoneNumber: phoneNumber || ""  // Send empty string if not available
            }

            // Set timeout warning after 30 seconds if still processing
            // Note: Users have up to 2 minutes to complete, so we warn at 30 seconds
            timeoutWarningTimer = setTimeout(function() {
                setTimeoutWarning(true);
                setLoadingMessage('Dette tar lengre tid enn vanlig. Vennligst vent...');
                logger.warn('Authentication taking longer than expected (30+ seconds)');
                // Show toastr with more details
                toastr.warning('Innloggingen tar lengre tid', 'Dette kan skyldes dårlig internettforbindelse. Hvis det tar for lang tid, kan du prøve å skanne QR-koden på nytt.');
            }, 30000);
            
            // Retry API call with exponential backoff for transient errors
            logger.logApiCall('POST', 'Vipps/AfterAuthentication', { deviceUUID: deviceUUID, hasCode: !!code });
            var data = await retryWithBackoff(
                async () => {
                    return await vippsService.validateBrowserAuthCode(model);
                },
                {
                    maxRetries: 2,
                    baseDelayMs: 1000,
                    maxDelayMs: 5000,
                    shouldRetry: isRetryableError,
                    onRetry: (attempt, maxRetries, delayMs, error) => {
                        logger.warn(`Retrying Vipps authentication (attempt ${attempt}/${maxRetries})`, { delayMs: delayMs, error: error.message || error });
                    }
                }
            );
            
            logger.logApiResponse('POST', 'Vipps/AfterAuthentication', data ? 200 : 500, { 
                hasErrorMessage: !!data?.errorMessage,
                success: !data?.errorMessage 
            });
            
            // Clear timeout warning timer
            clearTimeout(timeoutWarningTimer);
            setTimeoutWarning(false);
            setLoadingMessage('Fullfører innlogging...');
            
            // Clear URL parameters after processing
            clearUrlParameters();
            
            if (data != null && typeof (data) != 'string') {
                // Mark code as processed AFTER successful API call (not before)
                // This prevents race conditions where code is marked processed but API call fails
                if (code && !data.errorMessage) {
                    try {
                        vippsStateManager.markCodeAsProcessed(code);
                        logger.debug('Code marked as processed after successful API call', { code: code.substring(0, 10) + '...' });
                    } catch (e) {
                        logger.warn('Failed to mark code as processed (non-blocking)', { error: e });
                    }
                }
                
                setUserInfo(data);
                if (data.errorMessage) {
                    logger.logVippsEvent('authentication_failed', { 
                        deviceUUID: deviceUUID, 
                        errorMessage: data.errorMessage 
                    });
                    // Use improved error code mapping for API error messages
                    var errorInfo = getVippsErrorMessage(data.error, data.errorMessage);
                    var displayMessage = errorInfo.message;
                    if (errorInfo.userAction) {
                        displayMessage += ' ' + errorInfo.userAction;
                    }
                    setInfoMessage(displayMessage);
                    
                    // Check for expired/invalid session errors or non-retryable errors
                    var errorMsg = (data.errorMessage || '').toLowerCase();
                    var errorCode = (data.error || '').toLowerCase();
                    var shouldRedirect = errorMsg.includes('utløpt') || 
                                        errorMsg.includes('allerede brukt') || 
                                        errorMsg.includes('ugyldig') ||
                                        errorCode.includes('expired') ||
                                        errorCode.includes('invalid') ||
                                        !isErrorRetryable(data.error);
                    
                    if (shouldRedirect) {
                        // Error already displayed, state already cleared
                        // Redirect after a delay to allow user to read message
                        setTimeout(function() {
                            redirectToStartAuthentication();
                        }, 3000);
                    }
                } else {
                    logger.logVippsEvent('authentication_success', { deviceUUID: deviceUUID });
                    
                    // Cleanup session data after successful authentication
                    try {
                        sessionStorageService.removeDeviceUuid();
                        sessionStorageService.removeRedirectTracking();
                        // Keep session ID for backend correlation, but clear token/user
                        sessionManager.setToken(null);
                        sessionManager.setUser(null);
                        // Clear Vipps state after successful authentication
                        vippsStateManager.clearVippsState(deviceUUID, code);
                    } catch (e) {
                        logger.warn('Failed to clean up storage after success (non-blocking)', { error: e });
                    }
                }
            } else {
                // Handle API error response
                logger.error('API returned invalid response', null, { deviceUUID: deviceUUID });
                
                // Check if it's a network error
                var isNetworkError = data && (data.error === 'network_error' || data === null);
                var isBackendUnavailable = data && data.error === 'network_error' && data.message && data.message.includes('ERR_CONNECTION_REFUSED');
                
                var apiErrorInfo;
                if (isBackendUnavailable) {
                    apiErrorInfo = getVippsErrorMessage('backend_unavailable', 'Serveren er midlertidig utilgjengelig. Vennligst prøv igjen senere.');
                } else if (isNetworkError && isOffline) {
                    apiErrorInfo = getVippsErrorMessage('network_error', 'Ingen internettforbindelse. Sjekk nettverket ditt og prøv igjen.');
                } else {
                    apiErrorInfo = getVippsErrorMessage('network_error', 'Kunne ikke koble til serveren.');
                }
                
                setInfoMessage(apiErrorInfo.message + ' ' + apiErrorInfo.userAction);
                // Check if it's a timeout error (400)
                var isTimeout = data && data.error === 'timeout';
                dispatch(setAPIError(errorConstants.SET_ERROR_API, true, true, isTimeout ? 400 : null));
            }

            setIsLoader(false);
        } catch (error) {
            // Clean up timeout warning timer if error occurs
            if (timeoutWarningTimer) {
                clearTimeout(timeoutWarningTimer);
                setTimeoutWarning(false);
            }
            
            logger.error('Error handling Vipps callback', error, { 
                deviceUUID: deviceUUID || 'unknown',
                hasCode: !!code,
                hasState: !!state,
                url: window.location.href
            });
            
            // Rydd opp storage ved feil (alle data unntatt telefonnummer)
            try {
                if (deviceUUID) {
                    sessionStorageService.removeDeviceUuid();
                }
                // Rydd opp session data (session, token, user) - IKKE telefonnummer
                sessionManager.clearSession();
            } catch (e) {
                logger.warn('Failed to clean up storage on error (non-blocking)', { error: e });
            }
            
            clearUrlParameters();
            // Check if it's a timeout error (400)
            var isTimeout = error && (error.error === 'timeout' || error.message?.includes('timeout') || error.code === 'ECONNABORTED');
            dispatch(setAPIError(errorConstants.SET_ERROR_API, true, true, isTimeout ? 400 : null));
            setIsLoader(false);
        } finally {
            isProcessingVippsCallback = false;
        }
    }

    const deviceInfo = async (deviceId) => {
        var data = await deviceService.getDeviceByUUID(deviceId);
        if (typeof (data) != 'string') {
            var payload = {
                device: data.device,
                store: data.store
            }
            dispatch(setDeviceData(deviceConstants.SET_DEVICEINFO, payload));
        }
        return data;
    }

    const onTilbakButton = () => {
        var type = headerReducer?.userInfo?.loginType;
        if (type === AppConstant.smsName || type === AppConstant.deviceClassName)
            dispatch(setHeader(headerConstants.SET_HEADER_GREET, true));
        else
            dispatch(setError(errorConstants.SET_ERROR, true));

        history.push("/scan");
    }

    const success = () => {
        sessionManager.clearSession("success screen");
        return (
            <>
                <div className="d-flex align-item-center flex-direction-colomn rectangle green">
                    <h3 className="text-white mt-4"> Døren er låst opp </h3>
                    <img src={`${preLoadImagePath.successLoader}`} width="55px" alt="loader_green" />
                    <p className="text-white p-15"> {userInfo?.device_instruction}</p>
                </div>

                {renderSupport()}

                <div id="cctv" className="d-flex align-item-center flex-direction-colomn">
                    <img id="img_cctv" className="small-home-page" src={`${preLoadImagePath.cctv}`} alt="camera" />
                    <p>Butikken er kameraovervåket</p>
                </div>
            </>
        )
    }

    const renderSupport = () => {
        return (<div className="d-flex align-item-center flex-direction-colomn text-center mt-4 m-top-100">
            <h3> Melde feil? </h3>
            <p> {userInfo?.contact_instruction} </p>
            <h4> {userInfo?.contact}</h4>
        </div>);
    }

    const informationMessage = () => {
        return (
            <div id="infoBox">
                <div className="d-flex align-item-center justify-content-center flex-direction-colomn rectangle grey">
                    <h3 className="text-white rectangleBox"> {infoMessage}</h3>
                </div>
                {renderSupport()}
            </div>
        )
    }

    const renderCustomerInfo = () => {
        var type = headerReducer?.userInfo?.loginType;
        if (type === AppConstant.smsName)
            return <h2> Hei </h2>;
        else
            return <h2> Hei  {userInfo?.name}! </h2>;
    }

    const renderDetailPage = (userInfo) => {
        var userDetail = userInfo == null ? headerReducer?.userInfo : userInfo;
        if (userDetail == null || isObjectEmpty(userDetail))
            return (<></>);
        
        // CRITICAL SECURITY: Only show success screen for userInfo from actual Vipps authentication
        // Reject userInfo from Device endpoint or other non-Vipps sources
        // This prevents showing "Døren er låst opp" without actual Vipps authentication
        if (userDetail.loginType !== AppConstant.vippsAppName) {
            console.warn('Rejecting renderDetailPage for non-Vipps userInfo:', userDetail.loginType);
            return (
                <>
                    <div className="d-flex align-item-center flex-direction-colomn text-center gap-3">
                        <h2>Hei</h2>
                        <p className="px-2"> Vi tar vare på din <br /> kontaktinformasjon for <br /> mulig kontroll.</p>
                    </div>
                    <div className="d-flex align-item-center justify-content-center flex-direction-colomn rectangle grey">
                        <h3 className="text-white rectangleBox">Vennligst start autentisering på nytt</h3>
                    </div>
                    <div className="d-flex flex-direction-colomn gap-3 mt-auto small-home-page">
                        <button type="button" className="btn primary-btn custom-btn text-white btn-lg btn-block" onClick={onTilbakButton}>
                            <strong>Start på nytt</strong>
                        </button>
                    </div>
                </>
            );
        }
        
        return (
            <>
                <div className="d-flex align-item-center flex-direction-colomn text-center gap-3">
                    {renderCustomerInfo()}
                    <p className="px-2"> Vi tar vare på din <br /> kontaktinformasjon for <br /> mulig kontroll.</p>
                </div>

                {
                    userDetail && infoMessage == null
                        ?
                        success()
                        :
                        informationMessage()
                }
                <div className="d-flex flex-direction-colomn gap-3 mt-auto small-home-page">
                    <button type="button" className="btn primary-btn custom-btn text-white btn-lg btn-block" onClick={onTilbakButton}>
                        <strong>Start på nytt</strong>
                    </button>
                </div>
            </>
        )
    }

    return (
        <>
            <HeaderComponent />
            {(errorReducer.name === errorConstants.SET_ERROR || errorReducer.name === errorConstants.SET_ERROR_API) && errorReducer.isActive === true ? <ErrorsComponent /> :
                <div id="warpper-body" className="home-wrapper-body">
                    {
                        isLoader ?
                            <div className="d-flex align-item-center flex-direction-colomn gap-3">
                                <img src={`${preLoadImagePath.loader}`} width="100" />
                                {loadingMessage && (
                                    <p className="text-center text-white mt-2">{loadingMessage}</p>
                                )}
                                {timeoutWarning && (
                                    <div className="mt-3 text-center">
                                        <p className="text-warning">Innloggingen tar lengre tid enn vanlig. Vennligst vent...</p>
                                        <p className="text-muted small">Hvis dette tar for lang tid, kan du prøve å skanne QR-koden på nytt.</p>
                                    </div>
                                )}
                            </div>
                            :
                            renderDetailPage(userInfo)
                    }
                </div>
            }
            <FooterComponent />
        </>
    );
}


export { HomePage };